import {
  Box, Card, CardContent, Typography, TextField, Table, TableBody,
  TableCell, TableHead, TableRow, Chip, InputAdornment, Pagination,
  CircularProgress, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, FormControlLabel, Switch, Grid, MenuItem, Alert,
} from '@mui/material';
import { Search, Tune, Add, Remove, SwapHoriz } from '@mui/icons-material';
import { useState, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import api from '../../api/axios';
import { showSnackbar } from '../ui/uiSlice';

function TransferDialog({ product, open, onClose, onSuccess }) {
  const dispatch = useDispatch();
  const [branches, setBranches] = useState([]);
  const [fromBranch, setFromBranch] = useState('');
  const [toBranch, setToBranch] = useState('');
  const [qty, setQty] = useState(1);
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) api.get('/branches').then(r => setBranches(r.data.data || []));
  }, [open]);

  const handle = async () => {
    if (!fromBranch || !toBranch || !qty) { setError('All fields required'); return; }
    if (fromBranch === toBranch) { setError('Source and destination must differ'); return; }
    setLoading(true); setError('');
    try {
      await api.post('/inventory/transfer', { product_id: product.id, from_branch_id: +fromBranch, to_branch_id: +toBranch, quantity: +qty, note });
      dispatch(showSnackbar({ message: 'Stock transferred', severity: 'success' }));
      onSuccess();
    } catch (e) { setError(e.response?.data?.message || 'Transfer failed'); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Transfer Stock — {product?.name}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField select label="From Branch" value={fromBranch} onChange={e => setFromBranch(e.target.value)} fullWidth>
            {branches.map(b => <MenuItem key={b.id} value={b.id}>{b.name}</MenuItem>)}
          </TextField>
          <TextField select label="To Branch" value={toBranch} onChange={e => setToBranch(e.target.value)} fullWidth>
            {branches.map(b => <MenuItem key={b.id} value={b.id}>{b.name}</MenuItem>)}
          </TextField>
          <TextField label="Quantity" type="number" value={qty} onChange={e => setQty(e.target.value)} fullWidth inputProps={{ min: 1 }} />
          <TextField label="Note (optional)" value={note} onChange={e => setNote(e.target.value)} fullWidth />
          <Typography variant="caption" color="text.secondary">Current stock: {product?.stock}</Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handle} disabled={loading} startIcon={<SwapHoriz />}>
          {loading ? <CircularProgress size={20} /> : 'Transfer'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function InventoryPage() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [lowStock, setLowStock] = useState(false);
  const [loading, setLoading] = useState(false);
  const [adjustDialog, setAdjustDialog] = useState(false);
  const [transferDialog, setTransferDialog] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const { register, handleSubmit, reset } = useForm();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/inventory', { params: { page, limit: 20, search, low_stock: lowStock ? 1 : undefined } });
      setRows(data.data); setTotal(data.pagination.total);
    } finally { setLoading(false); }
  }, [page, search, lowStock]);

  useEffect(() => { load(); }, [load]);

  const openAdjust = (product) => {
    setSelectedProduct(product);
    reset({ quantity: 1, type: 'add', note: '' });
    setAdjustDialog(true);
  };

  const openTransfer = (product) => {
    setSelectedProduct(product);
    setTransferDialog(true);
  };

  const onAdjust = async (data) => {
    try {
      await api.post('/inventory/adjust', { product_id: selectedProduct.id, ...data });
      dispatch(showSnackbar({ message: 'Stock adjusted', severity: 'success' }));
      setAdjustDialog(false); load();
    } catch (e) {
      dispatch(showSnackbar({ message: 'Adjustment failed', severity: 'error' }));
    }
  };

  const stockColor = (row) => {
    if (row.stock <= 0) return 'error';
    if (row.stock <= row.low_stock_alert) return 'warning';
    return 'success';
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight={700}>{t('nav.inventory')}</Typography>
        <FormControlLabel
          control={<Switch checked={lowStock} onChange={e => { setLowStock(e.target.checked); setPage(1); }} color="warning" />}
          label="Low Stock Only"
        />
      </Box>

      <Card>
        <CardContent>
          <TextField
            placeholder={t('common.search')} size="small" sx={{ mb: 2, width: 320 }}
            value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }}
          />

          {loading ? <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box> : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('products.product')}</TableCell>
                  <TableCell>{t('products.sku')}</TableCell>
                  <TableCell>{t('products.category')}</TableCell>
                  <TableCell align="right">{t('products.salePrice')}</TableCell>
                  <TableCell align="center">{t('products.stock')}</TableCell>
                  <TableCell>Expiry</TableCell>
                  <TableCell>{t('common.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map(row => (
                  <TableRow key={row.id} hover sx={{ bgcolor: row.stock <= 0 ? 'error.light' : 'inherit' }}>
                    <TableCell><Typography variant="body2" fontWeight={600}>{row.name}</Typography></TableCell>
                    <TableCell>{row.sku}</TableCell>
                    <TableCell>{row.category}</TableCell>
                    <TableCell align="right">₨ {row.sale_price}</TableCell>
                    <TableCell align="center">
                      <Chip label={row.stock} size="small" color={stockColor(row)} />
                    </TableCell>
                    <TableCell>
                      {row.nearest_expiry ? (
                        <Chip
                          label={new Date(row.nearest_expiry).toLocaleDateString('en-PK')}
                          size="small"
                          color={new Date(row.nearest_expiry) < new Date(Date.now() + 30 * 86400000) ? 'error' : 'default'}
                        />
                      ) : '—'}
                    </TableCell>
                    <TableCell>
                      <Button size="small" variant="outlined" startIcon={<Tune />} onClick={() => openAdjust(row)} sx={{ mr: 0.5 }}>Adjust</Button>
                      <Button size="small" variant="outlined" color="secondary" startIcon={<SwapHoriz />} onClick={() => openTransfer(row)}>Transfer</Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
            <Pagination count={Math.ceil(total / 20)} page={page} onChange={(_, v) => setPage(v)} color="primary" />
          </Box>
        </CardContent>
      </Card>

      {/* Adjust dialog */}
      <Dialog open={adjustDialog} onClose={() => setAdjustDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Adjust Stock — {selectedProduct?.name}</DialogTitle>
        <DialogContent>
          <Box component="form" id="adj-form" onSubmit={handleSubmit(onAdjust)} sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField fullWidth select label="Type" defaultValue="add" {...register('type')}>
              <MenuItem value="add">Add Stock (Purchase / Found)</MenuItem>
              <MenuItem value="remove">Remove Stock (Damage / Loss)</MenuItem>
            </TextField>
            <TextField fullWidth label="Quantity" type="number" inputProps={{ min: 1 }} {...register('quantity', { min: 1 })} />
            <TextField fullWidth label="Reason (Damage / Expired / Recount…)" {...register('note')} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAdjustDialog(false)}>{t('common.cancel')}</Button>
          <Button variant="contained" type="submit" form="adj-form">{t('common.save')}</Button>
        </DialogActions>
      </Dialog>

      {/* Transfer dialog */}
      <TransferDialog
        product={selectedProduct}
        open={transferDialog}
        onClose={() => setTransferDialog(false)}
        onSuccess={() => { setTransferDialog(false); load(); }}
      />
    </Box>
  );
}
